(() => {
  'use strict';
  // Locale strings from layout/theme.liquid (window.abrReviewsStrings); English when missing.
  const abrText = (key, english, values) => {
    const strings = window.abrReviewsStrings || {};
    let text = typeof strings[key] === 'string' && strings[key] ? strings[key] : english;
    if (values) Object.keys(values).forEach((name) => { text = text.split(`[${name}]`).join(String(values[name])); });
    return text;
  };

  if (window.__adobeReviewFlowV16) return;
  window.__adobeReviewFlowV16 = true;

  const enhanced = new WeakSet();
  const modalStates = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pageSelector = '.jdgm-write-review-modal__page.current-page:not(.abrf-submit-ghost)';
  let lastModal = null;
  let lastModalTime = 0;
  let retainedImage = null;

  // The page Judge.me itself is on. While the share step is shown ahead
  // of About you, current-page is lent to the page on screen (see
  // applyView): Judge.me's own current page is then the one it was taken
  // from, unless Judge.me has since marked another page current itself.
  function nativePage(modal) {
    const pages = Array.from(modal.querySelectorAll(pageSelector));
    const own = pages.find((page) => !page.hasAttribute('data-abrf-lent-current'));
    return own || modal.querySelector('.jdgm-write-review-modal__page[data-abrf-took-current]') || pages[0] || null;
  }

  // The page on screen - usually Judge.me's own, except while the share
  // step is shown ahead of About you (see reorderedPages).
  function visiblePage(modal) {
    const page = nativePage(modal);
    const view = modalStates.get(modal)?.view;
    const pair = view && reorderedPages(modal);
    if (!pair) return page;
    if (view === 'share' && page === pair.details) return pair.media;
    if (view === 'submitting' && page === pair.media) return pair.details;
    return page;
  }

  function inputGroup(input) {
    return input?.closest('.jdgm-write-review-modal__field-group') ||
      input?.closest('.jdgm-write-review-modal__form-group') ||
      input?.parentElement || null;
  }

  function setFieldCopy(input, labelText, placeholder) {
    if (!(input instanceof HTMLElement)) return;
    if (placeholder) input.setAttribute('placeholder', placeholder);
    const group = inputGroup(input);
    const label = group?.querySelector('.jdgm-write-review-modal__field-label, label');
    if (label) label.textContent = labelText;
  }

  function replaceExactText(root, replacements) {
    if (!(root instanceof Element)) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      const value = node.nodeValue.trim().toLowerCase();
      if (!replacements.has(value)) return;
      const replacement = replacements.get(value);
      node.nodeValue = replacement ? node.nodeValue.replace(node.nodeValue.trim(), replacement) : '';
    });
  }

  function cloneProductImage(image) {
    if (!(image instanceof HTMLElement)) return null;
    const clone = image.cloneNode(true);
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.removeAttribute('href');
      element.setAttribute('tabindex', '-1');
    });
    const sources = image instanceof HTMLImageElement ? [image] : Array.from(image.querySelectorAll('img'));
    const copies = clone instanceof HTMLImageElement ? [clone] : Array.from(clone.querySelectorAll('img'));
    sources.forEach((source, index) => {
      const copy = copies[index];
      if (!(copy instanceof HTMLImageElement)) return;
      const resolvedSource = source.currentSrc || source.src;
      if (resolvedSource) {
        copy.removeAttribute('srcset');
        copy.removeAttribute('sizes');
        copy.src = resolvedSource;
      }
      copy.loading = 'eager';
      copy.decoding = 'sync';
    });
    return clone;
  }

  function elementRect(element) {
    if (!(element instanceof HTMLElement)) return null;
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height
    };
  }

  function unionRects(rects) {
    const valid = rects.filter((rect) => rect?.width && rect?.height);
    if (!valid.length) return null;
    const left = Math.min(...valid.map((rect) => rect.left));
    const top = Math.min(...valid.map((rect) => rect.top));
    const right = Math.max(...valid.map((rect) => rect.right));
    const bottom = Math.max(...valid.map((rect) => rect.bottom));
    return { top, left, width: right - left, height: bottom - top };
  }

  function visualRect(element, key) {
    if (!(element instanceof HTMLElement)) return null;
    if (key === 'stars') {
      const rect = unionRects(Array.from(element.querySelectorAll('.jdgm-star')).map((star) => star.getBoundingClientRect()));
      if (rect) return rect;
    }
    if (key === 'title' && element.textContent.trim()) {
      const range = document.createRange();
      range.selectNodeContents(element);
      const rect = unionRects(Array.from(range.getClientRects()));
      range.detach();
      if (rect) return rect;
    }
    return elementRect(element);
  }

  function clearSharedLayer(modal) {
    modal.querySelector('.abrf-shared-layer')?.remove();
  }

  function prepareSharedTransition(modal, page, direction) {
    const state = modalStates.get(modal);
    if (!state) return;
    const sourceRoot = direction === 'reverse'
      ? page.querySelector('.abrf-product-summary') || page
      : page;
    const image = sourceRoot.querySelector('.jdgm-write-review-modal__product-image');
    const title = sourceRoot.querySelector('.jdgm-write-review-modal__product-title');
    const starGroup = sourceRoot.querySelector('.jdgm-write-review-modal__stars');
    const stars = Array.from(starGroup?.querySelectorAll('.jdgm-star') || []);
    const snapshot = {
      image: visualRect(image, 'image'),
      title: visualRect(title, 'title'),
      stars: stars.map((star) => elementRect(star)),
      direction,
      createdAt: performance.now()
    };
    if (!state.imageClone) state.imageClone = cloneProductImage(image);
    retainedImage = state.imageClone;
    state.sharedSnapshot = snapshot;
    clearSharedLayer(modal);
    modal.classList.add('abrf-shared-pending');
    modal.classList.toggle('abrf-shared-reverse', direction === 'reverse');
    window.setTimeout(() => {
      const destinationStep = direction === 'reverse' ? 1 : 2;
      if (state.sharedSnapshot !== snapshot || state.step === destinationStep) return;
      state.sharedSnapshot = null;
      modal.classList.remove('abrf-shared-pending', 'abrf-shared-reverse');
      clearSharedLayer(modal);
    }, 1800);
  }

  function ensureClose(modal) {
    const content = modal.querySelector('.jdgm-write-review-modal__content');
    if (!content || content.querySelector(':scope > .abrf-close-btn')) return;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'abrf-close-btn';
    close.setAttribute('aria-label', abrText('close_form', 'Close review form'));
    close.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      dismissModal(modal);
    });
    content.append(close);
  }

  // Phones: drag the sheet down to close it. A drag starts from the grab
  // handle area, or anywhere on the sheet while its form is scrolled to
  // the top; past a quarter of its height (or a quick flick) it closes,
  // otherwise it springs back. Desktop never sees this.
  const sheetQuery = window.matchMedia('(max-width: 767px)');

  function bindSheetDrag(modal) {
    const content = modal.querySelector('.jdgm-write-review-modal__content');
    if (!content || content.abrfSheetBound) return;
    content.abrfSheetBound = true;
    let startY = 0;
    let startX = 0;
    let lastY = 0;
    let lastTime = 0;
    let velocity = 0;
    let offset = 0;
    let tracking = false;
    let dragging = false;

    const setOffset = (value) => {
      offset = value;
      modal.style.setProperty('--abrf-sheet-drag', `${value}px`);
    };

    content.addEventListener('touchstart', (event) => {
      const state = modalStates.get(modal);
      if (!sheetQuery.matches || reducedMotion.matches || event.touches.length !== 1 || !state || state.closing) return;
      const touch = event.touches[0];
      const fromHandle = touch.clientY - content.getBoundingClientRect().top < 44;
      if (!fromHandle && content.scrollTop > 0) return;
      if (event.target instanceof Element && event.target.closest('textarea, input, select')) return;
      tracking = true;
      dragging = false;
      startY = lastY = touch.clientY;
      startX = touch.clientX;
      lastTime = event.timeStamp;
      velocity = 0;
    }, { passive: true });

    content.addEventListener('touchmove', (event) => {
      if (!tracking || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const dy = touch.clientY - startY;
      if (!dragging) {
        if (Math.abs(touch.clientX - startX) > Math.abs(dy) || dy < 0) {
          if (Math.abs(dy) > 6 || Math.abs(touch.clientX - startX) > 6) tracking = false;
          return;
        }
        if (dy < 6) return;
        dragging = true;
        modal.classList.add('abrf-sheet-rested');
        modal.classList.remove('abrf-sheet-returning');
        modal.classList.add('abrf-sheet-dragging');
      }
      if (event.cancelable) event.preventDefault();
      const dt = Math.max(1, event.timeStamp - lastTime);
      velocity = (touch.clientY - lastY) / dt;
      lastY = touch.clientY;
      lastTime = event.timeStamp;
      setOffset(Math.max(0, dy - 6));
    }, { passive: false });

    const end = () => {
      if (!tracking) return;
      tracking = false;
      if (!dragging) return;
      dragging = false;
      const shouldClose = offset > content.offsetHeight * 0.25 || velocity > 0.5;
      if (shouldClose) {
        // The exit slides on from where the finger left it (the drag
        // offset feeds abrf-sheet-exit), then the offset is cleared.
        modal.classList.remove('abrf-sheet-dragging');
        modal.classList.add('abrf-sheet-flung');
        dismissModal(modal);
        setTimeout(() => {
          modal.style.removeProperty('--abrf-sheet-drag');
          modal.classList.remove('abrf-sheet-flung');
        }, 700);
        return;
      }
      modal.classList.add('abrf-sheet-returning');
      modal.classList.remove('abrf-sheet-dragging');
      content.style.transform = `translateY(${offset}px)`;
      void content.offsetHeight;
      content.style.transform = 'translateY(0px)';
      const settle = () => {
        modal.classList.remove('abrf-sheet-returning');
        content.style.removeProperty('transform');
        modal.style.removeProperty('--abrf-sheet-drag');
        offset = 0;
      };
      content.addEventListener('transitionend', function onEnd(event) {
        if (event.target !== content || event.propertyName !== 'transform') return;
        content.removeEventListener('transitionend', onEnd);
        settle();
      });
      setTimeout(settle, 500);
    };
    content.addEventListener('touchend', end, { passive: true });
    content.addEventListener('touchcancel', end, { passive: true });
  }

  function invokeNativeClose(modal, state) {
    const nativeClose = modal.querySelector('.jdgm-write-review-modal__close-btn');
    state.allowNativeClose = true;
    if (nativeClose instanceof HTMLElement) {
      nativeClose.click();
    } else {
      modal.querySelector('.jdgm-write-review-modal__overlay')?.click();
    }
    queueMicrotask(() => {
      state.allowNativeClose = false;
    });
  }

  // Fades the blurred backdrop and the card out first, then lets
  // Judge.me's own close hide the modal - calling it straight away hid
  // everything instantly, so there was never a fade-out. abrf-closing is
  // cleared by syncOverlayTransition once the modal is actually hidden (or
  // restored below if the native close didn't take).
  function dismissModal(modal) {
    const state = modalStates.get(modal);
    if (!state || state.closing) return;
    state.ratingIndex = -1;
    if (reducedMotion.matches || !modal.classList.contains('abrf-overlay-visible')) {
      invokeNativeClose(modal, state);
      return;
    }

    state.closing = true;
    // The page scrolls again as soon as the fade-out starts.
    unlockBodyScroll(modal);
    modal.classList.add('abrf-closing');
    modal.classList.remove('abrf-overlay-visible');
    const overlay = modal.querySelector('.jdgm-write-review-modal__overlay');
    let timer = 0;
    const onEnd = (event) => {
      if (event.target === overlay && event.propertyName === 'opacity') finish();
    };
    const finish = () => {
      if (!state.closing) return;
      state.closing = false;
      clearTimeout(timer);
      overlay?.removeEventListener('transitionend', onEnd);
      invokeNativeClose(modal, state);
      setTimeout(() => {
        if (!modalIsVisible(modal) || !modal.classList.contains('abrf-closing')) return;
        modal.classList.remove('abrf-closing');
        modal.classList.add('abrf-overlay-visible');
        lockBodyScroll(modal);
      }, 600);
    };
    overlay?.addEventListener('transitionend', onEnd);
    // Only a fallback for a missing transitionend, a little longer than the
    // fade: 380ms on the card, 460ms on the phone sheet's slower close.
    timer = setTimeout(finish, sheetQuery.matches ? 560 : 460);
  }

  // Phones: the sheet is as tall as its step (height: auto in the CSS), so
  // a step change would snap it to the new height. It glides there on the
  // sheet curve instead, growing or shrinking from its top edge like an
  // iOS sheet. sheetHeight is the height last laid out (kept by a
  // ResizeObserver): syncPage runs before the next layout, so that is
  // still the old step's.
  function watchSheetHeight(modal) {
    const content = modal.querySelector('.jdgm-write-review-modal__content');
    if (!content || typeof ResizeObserver !== 'function') return;
    new ResizeObserver((entries) => {
      const state = modalStates.get(modal);
      const size = entries[entries.length - 1].borderBoxSize?.[0];
      if (state) state.sheetHeight = size ? size.blockSize : content.offsetHeight;
    }).observe(content);
  }

  function resizeSheet(modal, state) {
    const content = modal.querySelector('.jdgm-write-review-modal__content');
    const from = state.sheetHeight;
    state.sheetResize?.cancel();
    state.sheetResize = null;
    if (!content || !from || !sheetQuery.matches || reducedMotion.matches || typeof content.animate !== 'function') return;
    const to = content.getBoundingClientRect().height;
    if (Math.abs(to - from) < 2) return;
    state.sheetResize = content.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: 460,
      easing: 'cubic-bezier(0.32, 0.72, 0, 1)'
    });
  }

  /*
   * The nav's space is reserved in the page's flex layout at all times
   * (see .abrf-rating-navigation in adobe-review-flow-v24.css) rather than
   * being added/removed from the DOM based on whether a rating is picked -
   * that used to change the page's total flex height, so the product
   * image/title/stars above it would shift and resize whenever the button
   * appeared or disappeared. Only its opacity/visibility toggles now, via
   * a CSS transition, so the layout never moves and there's nothing to
   * flash when this page becomes current again after going back from step
   * 2 (unlike revealIntroContent's per-element animate() calls, this
   * button was deliberately left out of that reveal).
   */
  function ensureRatingNext(modal, page) {
    const state = modalStates.get(modal);
    let navigation = page.querySelector('.abrf-rating-navigation');
    if (!navigation) {
      navigation = document.createElement('div');
      navigation.className = 'abrf-rating-navigation jdgm-write-review-modal__navigation';
      const action = document.createElement('button');
      action.type = 'button';
      action.className = 'abrf-rating-next jdgm-write-review-modal__nav-btn jdgm-write-review-modal__nav-btn-next';
      action.textContent = abrText('next', 'Next');
      action.addEventListener('click', () => {
        const currentState = modalStates.get(modal);
        if (!currentState || currentState.ratingIndex < 0) return;
        const stars = page.querySelectorAll('.jdgm-write-review-modal__stars .jdgm-star');
        const selectedStar = stars[currentState.ratingIndex];
        if (selectedStar instanceof HTMLElement) selectedStar.click();
      });
      navigation.append(action);
      page.append(navigation);
    }
    const hasRating = Boolean(state && state.ratingIndex >= 0);
    navigation.classList.toggle('is-visible', hasRating);
  }

  function trimGdprCopy(page) {
    const statement = page.querySelector('.jdgm-write-review-modal__gdpr-statement');
    if (!statement) return;
    const walker = document.createTreeWalker(statement, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const updated = node.nodeValue.replace(/We(?:'|’)?ll only contact you about your review if necessary\.\s*/i, '');
      if (updated !== node.nodeValue) node.nodeValue = updated;
    }
  }

  function ensureProductSummary(modal, page) {
    const bodyInput = page.querySelector('#jdgm-review-body-input');
    if (!bodyInput) return;

    let summary = page.querySelector('.abrf-product-summary');
    if (!summary) {
      summary = document.createElement('div');
      summary.className = 'abrf-product-summary';
      page.insertBefore(summary, page.firstElementChild);
    }

    const state = modalStates.get(modal);
    let image = summary.querySelector('.jdgm-write-review-modal__product-image') ||
      page.querySelector('.jdgm-write-review-modal__product-image');
    if (!image && state?.imageClone) image = state.imageClone.cloneNode(true);

    const title = summary.querySelector('.jdgm-write-review-modal__product-title') ||
      page.querySelector('.jdgm-write-review-modal__product-title');
    const stars = summary.querySelector('.jdgm-write-review-modal__stars') ||
      page.querySelector('.jdgm-write-review-modal__stars');

    const formerParents = new Set();
    [image, title, stars].forEach((element) => {
      if (element && element.parentElement !== summary) {
        if (element.parentElement && element.parentElement !== page) formerParents.add(element.parentElement);
        summary.append(element);
      }
    });
    // Whatever used to hold the image/title/stars (Judge.me's own product
    // row or rating group) stays behind with its padding/min-height but
    // nothing visible in it, which left a tall blank band above the
    // summary card. Hide any former parent (and its now-empty ancestors
    // inside this page) once it has no text or media left.
    formerParents.forEach((parent) => {
      for (let element = parent; element && element !== page && page.contains(element); element = element.parentElement) {
        if (element.contains(summary) || !isVisuallyEmpty(element)) break;
        element.classList.add('abrf-emptied');
      }
    });
  }

  function isVisuallyEmpty(element) {
    return !element.textContent.trim() &&
      !element.querySelector('img, svg, video, picture, input, textarea, select, button, .jdgm-star');
  }

  function clearFieldError(input) {
    if (!(input instanceof HTMLElement)) return;
    const group = inputGroup(input);
    const error = group?.querySelector('.abrf-field-error');
    error?.classList.remove('is-visible');
    group?.classList.remove('abrf-field-invalid');
    input.removeAttribute('aria-invalid');
    if (error && input.getAttribute('aria-describedby') === error.id) input.removeAttribute('aria-describedby');
  }

  function showFieldError(input) {
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
    const group = inputGroup(input);
    if (!group) return;
    let error = group.querySelector('.abrf-field-error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'abrf-field-error';
      error.id = `${input.id || 'abrf-field'}-error`;
      error.setAttribute('role', 'alert');
      error.setAttribute('aria-live', 'polite');
      error.textContent = abrText('required', 'Please fill out this field.');
      group.append(error);
    }
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', error.id);
    group.classList.add('abrf-field-invalid');
    requestAnimationFrame(() => error.classList.add('is-visible'));
  }

  function bindValidation(input) {
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) || input.dataset.abrfValidationBound) return;
    input.dataset.abrfValidationBound = 'true';
    input.addEventListener('input', () => {
      if (input.value.trim()) clearFieldError(input);
    });
  }

  function titleInputFor(page) {
    const input = page.querySelector(
      'input#jdgm-review-title-input:not([type="hidden"]), input[name*="title" i]:not([type="hidden"]):not(.abrf-title-proxy)'
    );
    return input instanceof HTMLInputElement ? input : null;
  }

  function ensureTitleInput(page) {
    const native = titleInputFor(page);
    if (!native) return null;
    const proxy = page.querySelector('.abrf-title-proxy');
    if (proxy instanceof HTMLInputElement && proxy.value && !native.value) native.value = proxy.value;
    proxy?.remove();
    native.classList.remove('abrf-native-title-input');
    native.classList.add('abrf-title-input', 'jdgm-write-review-modal__field-input');
    if (!native.id) native.id = 'abrf-review-title-input';
    native.placeholder = abrText('title_placeholder', 'Sum up your review');
    native.required = true;
    native.readOnly = false;
    native.disabled = false;
    native.removeAttribute('readonly');
    native.removeAttribute('disabled');
    native.removeAttribute('tabindex');
    native.removeAttribute('aria-hidden');
    native.setAttribute('aria-required', 'true');
    const label = inputGroup(native)?.querySelector('.jdgm-write-review-modal__field-label, label');
    if (label) label.setAttribute('for', native.id);
    return native;
  }

  function reorderReviewFields(page) {
    const titleInput = ensureTitleInput(page);
    const bodyInput = page.querySelector('#jdgm-review-body-input');
    if (!bodyInput) return;

    setFieldCopy(bodyInput, abrText('body_label', 'Your review'), abrText('body_placeholder', 'Share your experience with this product…'));
    setFieldCopy(titleInput, abrText('title_label', 'Review title'), abrText('title_placeholder', 'Sum up your review'));
    [titleInput, bodyInput].forEach((input) => {
      if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
      input.required = true;
      input.readOnly = false;
      input.removeAttribute('disabled');
      input.removeAttribute('tabindex');
      input.removeAttribute('aria-hidden');
      input.setAttribute('aria-required', 'true');
      bindValidation(input);
      inputGroup(input)?.querySelector('.jdgm-write-review-modal__field-label, label')?.classList.add('abrf-required-label');
    });

    const titleGroup = inputGroup(titleInput);
    const bodyGroup = inputGroup(bodyInput);
    if (
      titleGroup &&
      bodyGroup &&
      titleGroup !== bodyGroup &&
      titleGroup.parentElement === bodyGroup.parentElement &&
      titleGroup.nextElementSibling !== bodyGroup
    ) {
      bodyGroup.parentElement.insertBefore(titleGroup, bodyGroup);
    }
  }

  // Judge.me's Back/Next can live in one navigation shared by every page
  // rather than inside each page, so look for them across the whole modal
  // - skipping any that belong to a page other than this one. (Searching
  // only inside the page is why the share step's label and the hidden
  // Back never took effect live.)
  const nextButtonSelector = '.jdgm-write-review-modal__nav-btn-next, .jdgm-write-review-modal__navigation button:not(.jdgm-write-review-modal__nav-btn-back), .jdgm-write-review-modal__navigation input[type="submit"], .jdgm-write-review-modal__navigation input[type="button"]';
  function navButtonsFor(page) {
    const root = page.closest('.jdgm-write-review-modal') || page;
    return Array.from(root.querySelectorAll(nextButtonSelector)).filter((button) => {
      if (button.closest('.abrf-rating-navigation, .abrf-product-summary')) return false;
      const owner = button.closest('.jdgm-write-review-modal__page');
      return !owner || owner === page;
    });
  }

  // Puts back Judge.me's own label on buttons we relabelled (e.g. "Skip"
  // on the share step), once the visitor is on any other step.
  function restoreButtonCopy(page) {
    navButtonsFor(page).forEach((button) => {
      const original = button.dataset.abrfOriginalLabel;
      if (original === undefined) return;
      delete button.dataset.abrfOriginalLabel;
      if (button instanceof HTMLInputElement) button.value = original;
      else button.textContent = original;
      button.removeAttribute('aria-label');
    });
  }

  function setFinalButtonCopy(page, label = abrText('finish', 'Finish')) {
    // Match ensureSkipButton's selector below: lead with the bare
    // .nav-btn-next class (no .navigation ancestor required) rather than
    // assuming every page wraps its button in that exact wrapper class -
    // that assumption was never actually confirmed against this specific
    // page's live markup, only carried over from other pages.
    navButtonsFor(page).forEach((button) => {
      // Judge.me re-renders this button's own label from its internal state
      // after we run (see keepShareMediaControlsCorrect below), so this
      // gets called repeatedly on the same page. Writing textContent/value
      // unconditionally would recreate a text node every time - a mutation
      // even when the value doesn't change - which would keep re-triggering
      // the MutationObserver that calls back in here. Bail out once the
      // label already matches so repeated calls settle instead of looping.
      const current = button instanceof HTMLInputElement ? button.value : button.textContent;
      if (current === label) return;
      if (button.dataset.abrfOriginalLabel === undefined) button.dataset.abrfOriginalLabel = current;
      if (button instanceof HTMLInputElement) button.value = label;
      else button.textContent = label;
      button.setAttribute('aria-label', label);
    });
  }

  function isDetailsPage(page) {
    return Boolean(page.querySelector('#jdgm-email-input, #jdgm-name-input, input[type="email"]'));
  }

  // The post-submit "Thanks for your review!" screen is its own page (its
  // own data-page value, after share-media) but it isn't a step the user
  // steps through - it's an end state. The text check alone is fragile if
  // Judge.me fills that wording in only after a real submit (this page
  // might sit in the DOM from the start with no text yet while the user is
  // still on an earlier step) - the close-button class is structural and
  // much more likely to already be there regardless of timing.
  function isThankYouPage(page) {
    return Boolean(page.querySelector('.jdgm-write-review-modal__nav-btn-close'))
      || /thanks for your review|thank you for your review/i.test(page.textContent || '');
  }

  // By class, or by content: the live page wasn't carrying the
  // --share-media class, so none of this page's handling ever ran there.
  const youtubeInputSelector = 'input[placeholder*="youtube" i], input[name*="youtube" i], input[id*="youtube" i]';
  function isShareMediaPage(page) {
    return Boolean(page.querySelector(`input[type="file"], ${youtubeInputSelector}`)) ||
      (page.classList.contains('jdgm-write-review-modal__page--share-media') && !isDetailsPage(page));
  }

  /*
   * Share step before About you. Judge.me's own order is write review ->
   * about you -> share -> submit, and it validates the about-you fields
   * on its step 3 whatever that page shows, so its pages can't really be
   * reordered. Instead Judge.me keeps its order and only what is on
   * screen changes (state.view):
   *   Judge.me on About you, view 'share'   -> the share page is shown
   *     (its real inputs, so uploads and links land in Judge.me as usual)
   *   Next                                  -> view 'details', About you
   *   Next (Finish)                         -> Judge.me validates About
   *     you and moves to share; view 'submitting' keeps About you on
   *     screen while the share step is submitted straight away.
   * If that submit doesn't go through (e.g. an invalid YouTube link),
   * Judge.me's share page is shown with its error.
   */
  function reorderedPages(modal) {
    const pages = Array.from(modal.querySelectorAll('.jdgm-write-review-modal__page[data-page]'));
    const details = pages.find((p) => isDetailsPage(p));
    const media = pages.find((p) => p !== details && isShareMediaPage(p));
    if (!details || !media) return null;
    if (parseInt(media.dataset.page, 10) !== parseInt(details.dataset.page, 10) + 1) return null;
    return { details, media };
  }

  function updateView(modal, state) {
    const pair = reorderedPages(modal);
    const native = nativePage(modal);
    const previous = state.nativePage;
    state.nativePage = native;
    if (!pair || !native) {
      state.view = null;
    } else if (native === pair.details) {
      // Arriving from the write-review step starts on share; coming back
      // from Judge.me's share page (after a failed submit) lands on About you.
      if (previous !== pair.details) state.view = previous === pair.media ? 'details' : 'share';
    } else if (native === pair.media) {
      if (previous === pair.details && state.submitIntent) {
        state.submitIntent = false;
        state.view = 'submitting';
        if (state.ghost && !state.ghost.isConnected) modal.querySelector('.jdgm-write-review-modal__content')?.append(state.ghost);
        submitShareStep(modal, state, pair);
      } else if (state.view !== 'submitting') {
        state.view = null;
      }
    } else {
      state.view = null;
      state.submitIntent = false;
    }
    if (state.view !== 'submitting' && !(state.view === 'details' && state.submitIntent)) setSubmitting(modal, false);
    applyView(modal, pair);
  }

  function hasFlaggedError(page) {
    return Array.from(page.querySelectorAll('[class*="error"], [role="alert"]'))
      .some((element) => element.textContent.trim() && !(element instanceof HTMLInputElement) && element.getClientRects().length);
  }

  // Finish pressed: the card's contents fade out behind a spinner until
  // the thanks screen, so Judge.me's own page switching and the time the
  // submit takes are never seen. Undone if Judge.me turns the submit down.
  function setSubmitting(modal, on) {
    const content = modal.querySelector('.jdgm-write-review-modal__content');
    // Phones: the sheet holds its height behind the spinner while Judge.me
    // switches pages underneath, then glides to the thanks step's.
    if (content && on && !modal.classList.contains('abrf-submitting') && sheetQuery.matches) {
      content.style.height = `${content.getBoundingClientRect().height}px`;
    } else if (content && !on && content.style.height) {
      content.style.removeProperty('height');
    }
    modal.classList.toggle('abrf-submitting', on);
    const state = modalStates.get(modal);
    if (!on && state) {
      state.ghost?.remove();
      state.ghost = null;
    }
  }

  // A look-alike of About you, captured on Finish: Judge.me hides the
  // real page the instant it moves on, so this copy is what fades out.
  // Visual only - inert, and without ids/names so nothing reads it.
  function makeGhost(modal, page) {
    const content = modal.querySelector('.jdgm-write-review-modal__content');
    if (!content) return null;
    const rect = page.getBoundingClientRect();
    const box = content.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const ghost = page.cloneNode(true);
    const sources = page.querySelectorAll('input, textarea, select');
    ghost.querySelectorAll('input, textarea, select').forEach((field, index) => {
      const source = sources[index];
      if (source && 'value' in field) field.value = source.value;
      if (source && 'checked' in field) field.checked = source.checked;
    });
    [ghost, ...ghost.querySelectorAll('[id], [name], [for]')].forEach((element) => {
      element.removeAttribute('id');
      element.removeAttribute('name');
      element.removeAttribute('for');
    });
    ghost.removeAttribute('data-page');
    ghost.removeAttribute('data-abrf-lent-current');
    ghost.removeAttribute('data-abrf-took-current');
    ghost.classList.remove('abrf-shown', 'abrf-suppressed');
    ghost.classList.add('current-page', 'abrf-submit-ghost');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.inert = true;
    ghost.style.cssText = '';
    ghost.style.setProperty('display', getComputedStyle(page).display, 'important');
    ghost.style.setProperty('left', `${rect.left - box.left - content.clientLeft + content.scrollLeft}px`, 'important');
    ghost.style.setProperty('top', `${rect.top - box.top - content.clientTop + content.scrollTop}px`, 'important');
    ghost.style.setProperty('width', `${rect.width}px`, 'important');
    ghost.style.setProperty('height', `${rect.height}px`, 'important');
    return ghost;
  }

  function watchDetailsSubmit(modal, state, pair) {
    state.ghost?.remove();
    state.ghost = makeGhost(modal, pair.details);
    setSubmitting(modal, true);
    const started = Date.now();
    const check = window.setInterval(() => {
      if (!modal.isConnected || nativePage(modal) !== pair.details || state.view !== 'details') {
        window.clearInterval(check);
        return;
      }
      // Judge.me stayed on About you: it flagged a field (or, failing
      // any sign, it's been long enough) - bring the page back.
      if (!hasFlaggedError(pair.details) && Date.now() - started < 1200) return;
      window.clearInterval(check);
      state.submitIntent = false;
      setSubmitting(modal, false);
    }, 80);
  }

  function submitShareStep(modal, state, pair) {
    window.setTimeout(() => {
      const next = navButtonsFor(pair.media).find((button) => !button.disabled);
      if (next && nativePage(modal) === pair.media) {
        returnCurrent(modal);
        state.autoClick = true;
        next.click();
        state.autoClick = false;
      }
      // Still on Judge.me's share page and it flagged something (e.g. a
      // bad YouTube link), or it has stopped submitting (buttons enabled
      // again) without moving on: show that page. A slow submit keeps the
      // spinner, however long it takes.
      const started = Date.now();
      const check = window.setInterval(() => {
        if (state.view !== 'submitting' || nativePage(modal) !== pair.media) {
          window.clearInterval(check);
          return;
        }
        const busy = navButtonsFor(pair.media).some((button) => button.disabled);
        const stalled = !busy && Date.now() - started > 1500;
        if (!hasFlaggedError(pair.media) && !stalled) return;
        window.clearInterval(check);
        state.view = null;
        setSubmitting(modal, false);
        syncPage(modal);
      }, 100);
    }, 30);
  }

  // Shows the view's page in place of Judge.me's current one, borrowing
  // that page's display value. Set inline (!important), since Judge.me
  // may hide its pages with inline styles, which a stylesheet can't beat;
  // the value it had is put back afterwards. Idempotent, runs every sync.
  function holdDisplay(page, value) {
    if (!('abrfPriorDisplay' in page.dataset)) {
      page.dataset.abrfPriorDisplay = page.style.getPropertyValue('display');
      page.dataset.abrfPriorPriority = page.style.getPropertyPriority('display');
    }
    if (page.style.getPropertyValue('display') !== value || page.style.getPropertyPriority('display') !== 'important') {
      page.style.setProperty('display', value, 'important');
    }
    page.dataset.abrfHeldDisplay = value;
  }

  function releaseDisplay(page, isNative) {
    if (!('abrfHeldDisplay' in page.dataset)) return;
    const held = page.dataset.abrfHeldDisplay;
    const prior = page.dataset.abrfPriorDisplay;
    const priority = page.dataset.abrfPriorPriority;
    delete page.dataset.abrfHeldDisplay;
    delete page.dataset.abrfPriorDisplay;
    delete page.dataset.abrfPriorPriority;
    // Judge.me has set its own value since: leave it.
    if (page.style.getPropertyValue('display') !== held) return;
    if (isNative) {
      // Judge.me's current page: back to whatever showed it.
      if (prior && prior !== 'none') page.style.setProperty('display', prior, priority);
      else page.style.removeProperty('display');
    } else if (held !== 'none' || !prior) {
      // Not current: back to how Judge.me had it hidden.
      if (prior) page.style.setProperty('display', prior, priority);
      else page.style.removeProperty('display');
    }
    // (A hidden page whose prior value showed it stays hidden.)
  }

  // Judge.me hides pages that aren't current with more than display
  // (the share page showed up blank with display alone), so the page on
  // screen borrows current-page from Judge.me's own for as long as it is
  // shown; returnCurrent gives it back.
  function returnCurrent(modal) {
    modal.querySelectorAll('.jdgm-write-review-modal__page[data-abrf-lent-current]').forEach((page) => {
      page.removeAttribute('data-abrf-lent-current');
      page.classList.remove('current-page');
    });
    modal.querySelectorAll('.jdgm-write-review-modal__page[data-abrf-took-current]').forEach((page) => {
      page.removeAttribute('data-abrf-took-current');
      // Only if Judge.me hasn't moved on to another page meanwhile.
      if (!modal.querySelector(pageSelector)) page.classList.add('current-page');
    });
  }

  function lendCurrent(native, shown) {
    if (!native.hasAttribute('data-abrf-took-current')) native.setAttribute('data-abrf-took-current', '');
    if (native.classList.contains('current-page')) native.classList.remove('current-page');
    if (!shown.hasAttribute('data-abrf-lent-current')) shown.setAttribute('data-abrf-lent-current', '');
    if (!shown.classList.contains('current-page')) shown.classList.add('current-page');
  }

  function applyView(modal, pair) {
    const native = nativePage(modal);
    const shown = visiblePage(modal);
    const submitting = modalStates.get(modal)?.view === 'submitting';
    const swapped = !submitting && pair && native && shown && shown !== native;
    if (!swapped || !shown.hasAttribute('data-abrf-lent-current') || !native.hasAttribute('data-abrf-took-current')) {
      if (modal.querySelector('[data-abrf-lent-current], [data-abrf-took-current]')) returnCurrent(modal);
    }
    modal.querySelectorAll('.jdgm-write-review-modal__page.abrf-shown, .jdgm-write-review-modal__page.abrf-suppressed').forEach((page) => {
      if (swapped && page === shown) return;
      if (swapped && page === native) return;
      page.classList.remove('abrf-shown', 'abrf-suppressed');
      releaseDisplay(page, page === nativePage(modal));
    });
    if (!swapped) return;
    if (!native.classList.contains('abrf-suppressed')) {
      const display = getComputedStyle(native).display;
      shown.dataset.abrfViewDisplay = display && display !== 'none' ? display : 'flex';
      native.classList.add('abrf-suppressed');
    }
    lendCurrent(native, shown);
    holdDisplay(native, 'none');
    holdDisplay(shown, shown.dataset.abrfViewDisplay || 'flex');
    if (!shown.classList.contains('abrf-shown')) shown.classList.add('abrf-shown');
  }

  // The last step before submitting always reads Finish - with or
  // without media added on the share step.
  function shareMediaLabel() {
    return abrText('finish', 'Finish');
  }

  // Display name is meaningless once "post as anonymous" is checked, but
  // nothing disabled it - it stayed fully editable and enabled-looking.
  // Gray it out and make it genuinely unclickable while anonymous is on.
  function bindAnonymousToggle(page) {
    const checkbox = page.querySelector('.jdgm-write-review-modal__checkbox-input');
    const nameInput = page.querySelector('#jdgm-name-input');
    if (!(checkbox instanceof HTMLInputElement) || !(nameInput instanceof HTMLInputElement)) return;
    const sync = () => {
      nameInput.disabled = checkbox.checked;
    };
    if (!checkbox.dataset.abrfAnonymousBound) {
      checkbox.dataset.abrfAnonymousBound = 'true';
      checkbox.addEventListener('change', sync);
    }
    sync();
  }

  // Judge.me stamps each page with data-page (1, 2, 3, 4, ...) itself - use
  // that directly rather than guessing from field presence, which broke
  // once a page came along (share-media, the photo/video upload step) that
  // matches neither the "has a body textarea" nor the "has email/name
  // fields" heuristic and fell through to being treated as the rating page.
  function stepFor(page) {
    if (!page) return 1;
    // Share and About you trade step numbers (see reorderedPages), so the
    // dots, reveals and Finish label follow the order on screen.
    const modal = page.closest('.jdgm-write-review-modal');
    const pair = modal && reorderedPages(modal);
    if (pair && page === pair.details) return parseInt(pair.media.dataset.page, 10);
    if (pair && page === pair.media) return parseInt(pair.details.dataset.page, 10);
    const dataStep = parseInt(page.dataset.page, 10);
    if (Number.isFinite(dataStep) && dataStep > 0) return dataStep;
    if (page.querySelector('#jdgm-review-body-input')) return 2;
    if (isDetailsPage(page)) return 3;
    return 1;
  }

  function totalSteps(modal) {
    let max = 1;
    modal.querySelectorAll('.jdgm-write-review-modal__page[data-page]').forEach((p) => {
      if (isThankYouPage(p)) return;
      const n = parseInt(p.dataset.page, 10);
      if (Number.isFinite(n) && n > max) max = n;
    });
    return max;
  }

  // The rating step isn't counted - it's a threshold you cross before
  // getting into the actual multi-step form, not a step with progress of
  // its own. Dots run 1..N over write-review, about-you, share-media
  // (renders one dot per actual step, not hardcoded, so it still adapts
  // if a store's Judge.me config adds or removes a step).
  function ensureProgress(modal) {
    const content = modal.querySelector('.jdgm-write-review-modal__content');
    if (!content) return null;
    const total = Math.max(1, totalSteps(modal) - 1);
    let progress = content.querySelector(':scope > .abrf-progress');
    if (!progress) {
      progress = document.createElement('div');
      progress.className = 'abrf-progress';
      progress.setAttribute('role', 'img');
      content.append(progress);
    }
    if (progress.children.length !== total) {
      progress.innerHTML = Array.from({ length: total }, () => '<span></span>').join('');
    }
    return progress;
  }

  function updateProgress(modal, step, page) {
    const progress = ensureProgress(modal);
    if (!progress) return;
    const isRatingPage = step === 1;
    const isThankYou = Boolean(page && isThankYouPage(page));
    // Hidden (opacity, so it fades) on the rating step and the
    // confirmation screen - neither is part of the counted flow.
    progress.classList.toggle('is-visible', !isRatingPage && !isThankYou);
    if (isRatingPage || isThankYou) return;
    const displayStep = step - 1;
    const total = progress.children.length;
    progress.dataset.step = String(displayStep);
    progress.setAttribute('aria-label', abrText('step_of', 'Step [step] of [total]', { step: displayStep, total }));
    Array.from(progress.children).forEach((dot, index) => {
      dot.classList.toggle('is-active', index === displayStep - 1);
      dot.classList.toggle('is-complete', index < displayStep - 1);
    });
  }

  // The confirmation screen gets a success checkmark, loses the top-right
  // X (its own "Close" button is the only way to dismiss it), and its
  // content is force-centered as one group rather than whatever spacing
  // it inherits from the same page/navigation rules every other step uses.
  // The checkmark and our own Close button are placed on the card itself
  // (absolutely: checkmark in the exact centre, Close at the bottom) rather
  // than slotted into Judge.me's page markup, whose order and wrappers
  // kept putting the checkmark under the Close button. Judge.me's own
  // Close is hidden; ours closes with the same fade as the X.
  function decorateThankYouPage(modal, page) {
    modal.classList.add('abrf-thank-you');
    const closeBtn = modal.querySelector('.abrf-close-btn');
    if (closeBtn instanceof HTMLElement) closeBtn.style.display = 'none';
    const content = modal.querySelector('.jdgm-write-review-modal__content');
    if (!content) return;
    page.querySelector('.abrf-success-check')?.remove();
    if (!content.querySelector(':scope > .abrf-success-check')) {
      const check = document.createElement('div');
      check.className = 'abrf-success-check';
      check.setAttribute('aria-hidden', 'true');
      check.innerHTML = '<svg viewBox="0 0 48 48"><circle cx="24" cy="24" r="23"/><path pathLength="1" d="M14 24.5l7 7 13-14"/></svg>';
      content.append(check);
    }
    if (!content.querySelector(':scope > .abrf-thanks-close')) {
      const native = page.querySelector('.jdgm-write-review-modal__nav-btn-close');
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'abrf-thanks-close';
      close.textContent = (native?.textContent || native?.value || '').trim() || abrText('close', 'Close');
      close.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        dismissModal(modal);
      });
      content.append(close);
    }
  }

  function undecorateThankYouPage(modal) {
    modal.classList.remove('abrf-thank-you');
    const closeBtn = modal.querySelector('.abrf-close-btn');
    if (closeBtn instanceof HTMLElement) closeBtn.style.removeProperty('display');
    const content = modal.querySelector('.jdgm-write-review-modal__content');
    content?.querySelector(':scope > .abrf-success-check')?.remove();
    content?.querySelector(':scope > .abrf-thanks-close')?.remove();
  }

  // Uploading a photo/video is optional (nothing on this page is marked
  // Required, unlike email/name on the previous step) - give it an
  // explicit way to move on besides "click Finish without uploading
  // anything," which isn't obviously an option from looking at the page.
  // Searches the whole page for the next/finish button rather than
  // requiring a specific .navigation wrapper first, and wraps it together
  // with the new Skip button in a dedicated flex group - if the
  // surrounding nav uses justify-content: space-between (Back on one end,
  // Next on the other) for exactly two children, inserting Skip as a
  // third top-level child would land it stranded in the middle instead of
  // next to Next; grouping them keeps Skip visually attached to it
  // regardless of how the outer nav is laid out.
  // The "Submit" label and the Skip button on this page have both been
  // confirmed to disappear live after a single decoratePage() pass, despite
  // passing every static sandbox test - Judge.me's widget most likely
  // re-renders this page's navigation from its own internal state sometime
  // after we run. The main modal-wide MutationObserver only watches
  // class/style attribute changes, so it never notices a content re-render
  // like that. Watch this page's own subtree directly and keep re-applying
  // our changes whenever Judge.me touches it. ensureSkipButton and
  // setFinalButtonCopy are both no-ops once their target state is already
  // correct, so this settles after one extra pass rather than looping.
  const shareMediaPolling = new WeakSet();

  function keepShareMediaControlsCorrect(page) {
    // The last step before submitting reads "Finish"; the share step
    // keeps Judge.me's own "Next" if a store's order puts it earlier.
    const apply = () => {
      const modal = page.closest('.jdgm-write-review-modal');
      if (!modal || visiblePage(modal) !== page) return;
      // (Judge.me's own last step also counts: the share page shown after
      // a submit it didn't accept still submits.)
      const isFinal = !isThankYouPage(page) && (stepFor(page) === totalSteps(modal) ||
        (page === nativePage(modal) && parseInt(page.dataset.page, 10) === totalSteps(modal)));
      if (isFinal) setFinalButtonCopy(page, shareMediaLabel(page));
      else restoreButtonCopy(page);
    };
    apply();
    if (!page.dataset.abrfShareMediaInputs) {
      page.dataset.abrfShareMediaInputs = 'true';
      page.addEventListener('input', apply);
      page.addEventListener('change', apply);
    }
    if (!page.dataset.abrfShareMediaWatched) {
      page.dataset.abrfShareMediaWatched = 'true';
      const observer = new MutationObserver(apply);
      observer.observe(page, { childList: true, subtree: true, characterData: true });
    }
    // Belt-and-suspenders: three separate mutation-driven fixes for this
    // exact button have each checked out in sandbox testing and still not
    // held live, so something about how/when Judge.me touches this page
    // isn't being caught by any observer we've tried. A dumb poll doesn't
    // need to know why the change reverts - it just keeps stomping the
    // correct state back in every half second. It stops itself once this
    // page is no longer the one on screen (or gets removed) - tracked via
    // shareMediaPolling rather than the dataset flag above, so a later
    // re-visit to this page (after the poll for the first visit already
    // stopped itself) still gets its own fresh poll instead of relying on
    // a one-time flag that only ever fires on the very first visit.
    if (shareMediaPolling.has(page)) return;
    shareMediaPolling.add(page);
    const interval = window.setInterval(() => {
      const modal = page.closest('.jdgm-write-review-modal');
      if (!page.isConnected || !modal || visiblePage(modal) !== page) {
        window.clearInterval(interval);
        shareMediaPolling.delete(page);
        return;
      }
      apply();
    }, 500);
  }

  function decoratePage(modal, page) {
    const step = stepFor(page);
    if (step === 2) {
      ensureProductSummary(modal, page);
      reorderReviewFields(page);
      trimGdprCopy(page);
    } else if (step === 1) {
      ensureRatingNext(modal, page);
    }

    if (isDetailsPage(page)) {
      replaceExactText(page, new Map([
        ['we respect your privacy', ''],
        ['we respect your privacy.', ''],
        ['agree to our privacy policy', ''],
        ['agree to our privacy policy.', '']
      ]));
      bindAnonymousToggle(page);
    }

    // isShareMediaPage's class check has been reliable for identifying
    // *which* page this is (it's what fixed the earlier phantom-nav bug),
    // but three straight fixes for this button still haven't held live -
    // OR it with the numeric "this is the last real step" check as a
    // second, independent signal, in case the class ever isn't present
    // yet at the moment this runs. In the current flow (share-media is the
    // last step before the thank-you page) these should always agree.
    const isFinalStep = !isThankYouPage(page) && step === totalSteps(modal);
    if (isShareMediaPage(page) || isFinalStep) {
      // This is always the last step before submitting, regardless of
      // where it falls in data-page numbering - target it directly rather
      // than relying solely on totalSteps() figuring out "the last step",
      // which depends on correctly recognizing the confirmation screen and
      // has been wrong more than once. keepShareMediaControlsCorrect also
      // re-applies itself if Judge.me re-renders this page later.
      keepShareMediaControlsCorrect(page);
    } else if (isThankYouPage(page)) {
      decorateThankYouPage(modal, page);
    } else {
      if (modal.classList.contains('abrf-thank-you')) undecorateThankYouPage(modal);
      restoreButtonCopy(page);
    }

    page.classList.add('abrf-page-ready');
  }

  function inverseFrames(source, target) {
    const translateX = Math.round((source.left + source.width / 2 - target.left - target.width / 2) * 100) / 100;
    const translateY = Math.round((source.top + source.height / 2 - target.top - target.height / 2) * 100) / 100;
    const scaleX = Math.round((source.width / target.width) * 10000) / 10000;
    const scaleY = Math.round((source.height / target.height) * 10000) / 10000;
    return [
      {
        opacity: 1,
        transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})`
      },
      {
        opacity: 1,
        transform: 'translate3d(0, 0, 0) scale(1)'
      }
    ];
  }

  // Only used to stop revealIntroContent (the rating step's header/intro)
  // from re-flashing when navigating back to it - see the comment there.
  // revealReviewFields deliberately has no such guard: forward navigation
  // into a page should always play its reveal, every time, not just the
  // first time ever.
  const revealedPages = new WeakSet();

  // Rows rising in from below briefly overflow whatever scrolls around
  // them, so its scrollbar flashed in and out as each page appeared. Pause
  // scrolling on those containers for the length of the entrance.
  function holdScrollbars(page, duration) {
    // A hold still running on this page is undone first, so the values
    // recorded below are the page's own, not the held 'hidden'.
    page.abrfScrollbarRestore?.();
    const content = page.closest('.jdgm-write-review-modal__content');
    const held = [];
    for (let element = page; element; element = element.parentElement) {
      const overflowY = getComputedStyle(element).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') held.push([element, element.style.getPropertyValue('overflow-y'), element.style.getPropertyPriority('overflow-y')]);
      if (element === content) break;
    }
    // Every scroll area on the page, including ones that overflow at this
    // moment (the page may still be settling). Scrollbars themselves are
    // hidden in CSS; this keeps the entrance from being scrolled mid-way.
    page.querySelectorAll('*').forEach((element) => {
      const overflowY = getComputedStyle(element).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') held.push([element, element.style.getPropertyValue('overflow-y'), element.style.getPropertyPriority('overflow-y')]);
    });
    held.forEach(([element]) => element.style.setProperty('overflow-y', 'hidden', 'important'));
    const restore = () => {
      window.clearTimeout(timer);
      if (page.abrfScrollbarRestore === restore) page.abrfScrollbarRestore = null;
      held.forEach(([element, value, priority]) => {
        if (value) element.style.setProperty('overflow-y', value, priority);
        else element.style.removeProperty('overflow-y');
      });
    };
    const timer = window.setTimeout(restore, duration);
    page.abrfScrollbarRestore = restore;
  }

  function revealReviewFields(page) {
    if (reducedMotion.matches) return;
    const groupSelector = page.querySelector('.jdgm-write-review-modal__field-group')
      ? '.jdgm-write-review-modal__field-group'
      : '.jdgm-write-review-modal__form-group';
    const fields = Array.from(page.querySelectorAll(
      `.jdgm-write-review-modal__page-header, ${groupSelector}, .jdgm-write-review-modal__gdpr-statement`
    )).filter((element) => !element.closest('.abrf-product-summary'));
    // The buttons take the slot after the last row (revealNavigation).
    const navigationDelay = 110 + Math.min(fields.length, 5) * 55;
    holdScrollbars(page, navigationDelay + 60 + 620);

    // A pure opacity fade with no stagger reads as flat rather than
    // "smooth" - Apple's own reveal motion pairs a small, deliberate
    // per-element offset with a bit of vertical travel, not just a color
    // change. The offset here (45ms) is small enough that it feels like one
    // coordinated reveal rather than a scan down the page.
    fields.forEach((element, index) => {
      if (!(element instanceof HTMLElement) || typeof element.animate !== 'function') return;
      // Apple-style: a soft expo-out with a little more travel, each row
      // following the last by 55ms (capped so long pages don't drag).
      const animation = element.animate([
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], {
        duration: 560,
        delay: 110 + Math.min(index, 5) * 55,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        fill: 'both'
      });
      animation.onfinish = () => animation.cancel();
    });
    revealNavigation(page, navigationDelay);
  }

  // Back and Next come in last, wherever Judge.me renders them: its
  // navigation can sit outside the page, where the rows above never
  // reached it, so Next just appeared. Back rises with the rows; Next, the
  // step's main action, follows a beat later, growing in slightly and
  // settling with a soft spring (a touch of overshoot).
  function revealNavigation(page, delay) {
    const modal = page.closest('.jdgm-write-review-modal');
    if (!modal) return;
    const shown = (element) => element instanceof HTMLElement && typeof element.animate === 'function'
      && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
    const backs = Array.from(modal.querySelectorAll('.jdgm-write-review-modal__nav-btn-back')).filter((button) => {
      const owner = button.closest('.jdgm-write-review-modal__page');
      return (!owner || owner === page) && !button.closest('.abrf-product-summary') && shown(button);
    });
    backs.forEach((button) => {
      const animation = button.animate([
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 560, delay, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'both' });
      animation.onfinish = () => animation.cancel();
    });
    navButtonsFor(page).filter(shown).forEach((button) => {
      const animation = button.animate([
        { opacity: 0, transform: 'translateY(8px) scale(0.94)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' }
      ], { duration: 620, delay: delay + 60, easing: 'cubic-bezier(0.34, 1.36, 0.64, 1)', fill: 'both' });
      animation.onfinish = () => animation.cancel();
    });
  }

  // The rating step only has one entry point that isn't a "return to a
  // page you've already seen": the very first time the modal opens. Every
  // other time it's reached is via Back from write-review, where the
  // header/Next button are already sitting there filled in - so this only
  // ever plays once per page element, unlike revealReviewFields.
  function revealIntroContent(page) {
    if (reducedMotion.matches || revealedPages.has(page)) return;
    revealedPages.add(page);
    const elements = page.querySelectorAll(
      '.jdgm-write-review-modal__page-header, .jdgm-write-review-modal__intro'
    );
    elements.forEach((element, index) => {
      if (!(element instanceof HTMLElement) || typeof element.animate !== 'function') return;
      // A touch slower than the card itself (was 420ms / 45ms stagger /
      // 6px) so the title and description settle in rather than snap.
      const animation = element.animate([
        { opacity: 0, transform: 'translateY(8px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], {
        duration: 560,
        delay: 120 + index * 70,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both'
      });
      animation.onfinish = () => animation.cancel();
    });
  }

  /*
   * One consistent "header animation" (fade + small settle) for every step
   * that isn't the rating<->writing shared-element transition. The rating
   * step (no fields, just header/intro) uses revealIntroContent; every
   * other step has its own input fields, so it uses revealReviewFields to
   * fade those in too, not just the header. Callers are expected to only
   * invoke this on forward navigation - see the isBackward check in
   * syncPage, which skips calling this entirely when moving to a page
   * that's already been filled in (e.g. Back from the details step to
   * write-review shouldn't re-fade content that's already sitting there).
   */
  function revealPageContent(modal, page) {
    const state = modalStates.get(modal);
    // The thanks step has its own CSS entrance (title, checkmark, Close).
    if (isThankYouPage(page)) return;
    if (state?.step === 1) {
      revealIntroContent(page);
    } else {
      revealReviewFields(page);
    }
  }

  function animateSharedProduct(modal, page) {
    const state = modalStates.get(modal);
    const snapshot = state?.sharedSnapshot;
    const destinationRoot = state.step === 2
      ? page.querySelector('.abrf-product-summary')
      : page;

    if (reducedMotion.matches || !snapshot || performance.now() - snapshot.createdAt > 1600 || !destinationRoot) {
      modal.classList.remove('abrf-shared-pending', 'abrf-shared-reverse');
      clearSharedLayer(modal);
      revealPageContent(modal, page);
      return;
    }

    {
      const targets = {
        image: destinationRoot.querySelector('.jdgm-write-review-modal__product-image'),
        title: destinationRoot.querySelector('.jdgm-write-review-modal__product-title'),
        starGroup: destinationRoot.querySelector('.jdgm-write-review-modal__stars')
      };
      const targetStars = Array.from(targets.starGroup?.querySelectorAll('.jdgm-star') || []);

      const measured = {
        image: visualRect(targets.image, 'image'),
        title: visualRect(targets.title, 'title'),
        stars: targetStars.map((star) => elementRect(star))
      };

      // Apple's standard curve: quick to get going, long gentle settle.
      const easing = 'cubic-bezier(0.32, 0.72, 0, 1)';
      const animations = [];
      const animateTarget = (element, source, target, delay = 0) => {
        if (!(element instanceof HTMLElement) || !source || !target || typeof element.animate !== 'function') return;
        const elementBox = element.getBoundingClientRect();
        const originX = target.left + target.width / 2 - elementBox.left;
        const originY = target.top + target.height / 2 - elementBox.top;
        element.style.transformOrigin = `${originX}px ${originY}px`;
        const animation = element.animate(inverseFrames(source, target), {
          duration: 600,
          delay,
          easing,
          fill: 'both'
        });
        animations.push({ animation, element });
      };

      // The image and the stars fly; the stars follow each other by 20ms
      // so they flow into place rather than moving as one block.
      animateTarget(targets.image, snapshot.image, measured.image);
      targetStars.forEach((star, index) => animateTarget(star, snapshot.stars[index], measured.stars[index], index * 20));

      // The title doesn't fly: the layout goes from a centered stack to a
      // row, so its straight-line path crossed the image mid-flight (and
      // scaling text looks soft). It fades up in place just after the
      // image and stars set off.
      if (targets.title instanceof HTMLElement && typeof targets.title.animate === 'function') {
        const animation = targets.title.animate([
          { opacity: 0, transform: 'translateY(6px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 420, delay: 140, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' });
        animations.push({ animation, element: targets.title });
      }

      // The summary card's own chrome (background, border, shadow) fades
      // in while the image/stars land in it, instead of popping in at full
      // strength the moment the handoff starts.
      const summary = state.step === 2 ? destinationRoot : null;
      if (summary instanceof HTMLElement && typeof summary.animate === 'function') {
        const computed = getComputedStyle(summary);
        const animation = summary.animate([
          { backgroundColor: 'rgba(255, 255, 255, 0)', borderColor: 'rgba(255, 255, 255, 0)', boxShadow: 'none' },
          { backgroundColor: computed.backgroundColor, borderColor: computed.borderColor, boxShadow: computed.boxShadow }
        ], { duration: 480, delay: 60, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' });
        animations.push({ animation, element: summary });
      }

      modal.classList.remove('abrf-shared-pending');
      if (state.step === 2) {
        revealReviewFields(page);
      } else {
        revealIntroContent(page);
      }
      state.sharedSnapshot = null;
      Promise.all(animations.map(({ animation }) => animation.finished.catch(() => null))).finally(() => {
        requestAnimationFrame(() => {
          animations.forEach(({ animation, element }) => {
            animation.cancel();
            element.style.removeProperty('transform-origin');
          });
          clearSharedLayer(modal);
          modal.classList.remove('abrf-shared-reverse');
        });
      });
    }
  }

  function modalIsVisible(modal) {
    if (!modal.isConnected) return false;
    const style = getComputedStyle(modal);
    return style.display !== 'none' && style.visibility !== 'hidden' && modal.getClientRects().length > 0;
  }

  /*
   * The backdrop is a constant blur + dim on the overlay whose opacity
   * fades (see adobe-review-flow-v24.css) - the blur itself never
   * animates. This function just flips the class. The double-rAF before
   * adding abrf-overlay-visible forces the transparent state to actually
   * render as a frame first - without it, a fast reopen can coalesce the
   * reset and the open-class-add into one style recalc with nothing to
   * transition from, and it snaps instead of fading.
   */
  // The page is held still with theme.scrollLock (theme.js), which keeps
  // the scrollbar: hiding it shifted the layout as the modal opened and
  // closed, and left a white bar in the gutter the theme reserves. Each
  // modal is its own owner, so this pairs up however often it runs.
  function lockBodyScroll(modal) {
    window.theme?.scrollLock?.lock(modal);
  }

  function unlockBodyScroll(modal) {
    window.theme?.scrollLock?.unlock(modal);
  }

  // Any ancestor of the blurred overlay that is mid-fade (opacity < 1)
  // stops the blur from rendering until the fade ends - see the CSS note
  // above .jdgm-write-review-modal.abr-review-flow. The modal itself is
  // pinned in CSS; this covers anything else above it: finish its opacity
  // animations/transitions and hold it at full opacity while the modal is
  // open, restoring the original inline value on close.
  function neutralizeAncestorFades(modal) {
    const state = modalStates.get(modal);
    const overlay = modal.querySelector('.jdgm-write-review-modal__overlay');
    if (!state || !overlay) return;
    state.pinnedOpacity ??= [];
    for (let element = overlay.parentElement; element && element !== document.documentElement; element = element.parentElement) {
      element.getAnimations?.().forEach((animation) => {
        const animatesOpacity = animation.transitionProperty === 'opacity' ||
          (animation.effect?.getKeyframes?.() || []).some((keyframe) => 'opacity' in keyframe);
        if (!animatesOpacity) return;
        try { animation.finish(); } catch (error) { /* infinite animation - leave it */ }
      });
      if (parseFloat(getComputedStyle(element).opacity) < 1 && !state.pinnedOpacity.some(([pinned]) => pinned === element)) {
        state.pinnedOpacity.push([element, element.style.getPropertyValue('opacity'), element.style.getPropertyPriority('opacity')]);
        element.style.setProperty('opacity', '1', 'important');
      }
    }
  }

  function releaseAncestorFades(modal) {
    const state = modalStates.get(modal);
    if (!state?.pinnedOpacity?.length) return;
    state.pinnedOpacity.forEach(([element, value, priority]) => {
      if (value) element.style.setProperty('opacity', value, priority);
      else element.style.removeProperty('opacity');
    });
    state.pinnedOpacity = [];
  }

  // Bottom edge of the site header, if it's on screen, so the blurred
  // overlay can start below it and leave the header unaffected.
  function headerOffset() {
    const header = document.querySelector('header.header');
    if (!header) return 0;
    const section = header.closest('.shopify-section') || header;
    const bottom = Math.max(header.getBoundingClientRect().bottom, section.getBoundingClientRect().bottom);
    return bottom > 0 && bottom < window.innerHeight / 2 ? Math.round(bottom) : 0;
  }

  function syncOverlayTransition(modal) {
    const state = modalStates.get(modal);
    if (!state) return;
    const visible = modalIsVisible(modal);

    if (!visible) {
      const wasVisible = state.overlayVisible;
      state.overlayVisible = false;
      if (state.overlayFrame) cancelAnimationFrame(state.overlayFrame);
      state.overlayFrame = 0;
      if (modal.classList.contains('abrf-closing')) modal.classList.remove('abrf-closing');
      // classList.remove() unconditionally rewrites the class attribute
      // even when the token is already absent, which still fires this
      // MutationObserver (it watches class/style) - calling it every time
      // this runs, with no guard, is an infinite self-triggering loop that
      // pegs the main thread and makes the whole page unresponsive. Only
      // touch the DOM when there's an actual change to make.
      // (dismissModal removes the class itself at the start of its
      // fade-out, so the scroll unlock keys off the tracked state, not the
      // class.)
      if (modal.classList.contains('abrf-overlay-visible')) {
        modal.classList.remove('abrf-overlay-visible');
      }
      if (wasVisible) unlockBodyScroll(modal);
      if (modal.classList.contains('abrf-sheet-rested')) modal.classList.remove('abrf-sheet-rested');
      releaseAncestorFades(modal);
      return;
    }

    if (state.overlayVisible) return;
    state.overlayVisible = true;
    modal.style.setProperty('--abrf-header-offset', `${headerOffset()}px`);
    lockBodyScroll(modal);
    neutralizeAncestorFades(modal);
    requestAnimationFrame(() => {
      if (modalIsVisible(modal)) neutralizeAncestorFades(modal);
    });

    if (reducedMotion.matches) {
      modal.classList.add('abrf-overlay-visible');
      return;
    }

    modal.classList.remove('abrf-overlay-visible');
    state.overlayFrame = requestAnimationFrame(() => {
      state.overlayFrame = requestAnimationFrame(() => {
        state.overlayFrame = 0;
        if (modalIsVisible(modal)) modal.classList.add('abrf-overlay-visible');
      });
    });
  }

  function syncPage(modal) {
    syncOverlayTransition(modal);
    const state = modalStates.get(modal);
    if (state) updateView(modal, state);
    const page = visiblePage(modal);
    if (!page) return;
    ensureClose(modal);
    const step = stepFor(page);

    decoratePage(modal, page);
    updateProgress(modal, step, page);
    // toggle(..., force) is a no-op when already in that state, so this
    // doesn't re-trigger the observer.
    modal.classList.toggle('abrf-on-review-step', step === 2);

    if (state.currentPage !== page) {
      const previousStep = state.step;
      const stepChanged = previousStep !== step;
      // Any step-2/3/4-and-up transition that isn't the rating<->writing
      // shared-element handoff falls through here. A lower step number
      // than before means this is a Back navigation to a page that's
      // already filled in - don't replay its reveal animation.
      const isBackward = stepChanged && step < previousStep;
      state.currentPage = page;
      state.step = step;
      // Before the shared handoff below measures where the product lands:
      // it must measure the sheet at the height it starts gliding from.
      resizeSheet(modal, state);
      const hasForwardTransition = step === 2 && state.sharedSnapshot?.direction === 'forward';
      const hasReverseTransition = step === 1 && state.sharedSnapshot?.direction === 'reverse';
      if (stepChanged && (hasForwardTransition || hasReverseTransition)) {
        animateSharedProduct(modal, page);
      } else if (stepChanged) {
        modal.classList.remove('abrf-shared-pending', 'abrf-shared-reverse');
        clearSharedLayer(modal);
        if (!isBackward) revealPageContent(modal, page);
      } else {
        modal.classList.remove('abrf-shared-pending', 'abrf-shared-reverse');
        clearSharedLayer(modal);
      }
    }
  }

  function queueSync(modal) {
    const state = modalStates.get(modal);
    if (!state) return;
    // Mid-handoff (a star was just picked, or Back pressed), sync right
    // away: this runs from the MutationObserver, i.e. before the next
    // paint, so the destination's image/stars are already sitting at their
    // start positions in the very first frame. Waiting a frame here left
    // them hidden (abrf-shared-pending) for a frame or two after the click,
    // which read as the stars blinking out and back before moving.
    // Only when the visible page has actually switched: syncPage itself
    // mutates the DOM, so syncing immediately on every mutation while a
    // snapshot is pending (e.g. Judge.me filling the stars before it
    // swaps pages) would re-trigger this observer in a tight loop.
    if (visiblePage(modal) !== state.currentPage) {
      if (state.frame) cancelAnimationFrame(state.frame);
      state.frame = 0;
      syncPage(modal);
      return;
    }
    if (state.frame) return;
    state.frame = requestAnimationFrame(() => {
      state.frame = 0;
      syncPage(modal);
      // Some of syncPage's writes always queue a mutation even when nothing
      // changes (decoratePage's classList.add), so every pass schedules the
      // next one through the modal observer: a per-frame pass while a page is
      // current. A removed modal kept that going forever (one more 60/s loop
      // per replaced modal). Once it is detached, drop the records of this
      // pass's own writes; any other change to it still schedules a pass, and
      // enhance() resumes the per-frame pass if it is re-inserted.
      if (!modal.isConnected) state.observer?.takeRecords();
    });
  }

  // Both navigation buttons disabled (before the thanks screen): the whole
  // navigation fades out (data-abrf-nav-idle, see the CSS). This used to be
  // a `:has(.nav-btn-next:disabled):has(.nav-btn-back:disabled)` rule; a
  // :has() testing :disabled in a stylesheet every product page loads made
  // each disabled toggle anywhere on the page (gallery arrows, variant
  // changes, quantity buttons) restyle ~100 more elements. Mutation observer
  // callbacks run before the next style update, so the attribute lands in
  // the same frame the rule matched in. An attribute, not a class: the
  // modal observer below only listens to class/style changes.
  function syncNavigationIdle(modal) {
    modal.querySelectorAll('.jdgm-write-review-modal__navigation').forEach((navigation) => {
      const idle = !!navigation.querySelector('.jdgm-write-review-modal__nav-btn-next:disabled')
        && !!navigation.querySelector('.jdgm-write-review-modal__nav-btn-back:disabled');
      if (idle !== navigation.hasAttribute('data-abrf-nav-idle')) navigation.toggleAttribute('data-abrf-nav-idle', idle);
    });
  }

  // <html> carries abrf-modal-present while an enhanced modal is in the page:
  // the scroll-lock overflow fix in adobe-reviews.css keys on it instead of
  // on html.scroll-locked:has(...), which restyled the whole page on every
  // element added while any drawer held the page still. Mutation observer
  // callbacks run before the next style update, so it lands in the same
  // frame the rule matched in.
  const flowModals = document.getElementsByClassName('abr-review-flow');

  function syncModalPresent() {
    const present = Array.prototype.some.call(flowModals, (modal) => modal.classList.contains('jdgm-write-review-modal'));
    const root = document.documentElement;
    if (present !== root.classList.contains('abrf-modal-present')) root.classList.toggle('abrf-modal-present', present);
  }

  function enhance(modal) {
    if (!(modal instanceof HTMLElement)) return;
    if (enhanced.has(modal)) {
      syncModalPresent();
      // The same modal re-inserted after being detached: pick the per-frame
      // pass back up where it would have kept running (it only runs while a
      // page is current), exactly as if it had never paused.
      if (modal.isConnected && visiblePage(modal)) queueSync(modal);
      return;
    }
    const now = performance.now();
    const isReplacement = Boolean(lastModal && !lastModal.isConnected && now - lastModalTime < 1800);
    enhanced.add(modal);
    modalStates.set(modal, { currentPage: null, imageClone: retainedImage, frame: 0, overlayFrame: 0, overlayVisible: false, allowNativeClose: false, step: 0, sharedSnapshot: null, ratingIndex: -1, observer: null });
    modal.classList.add('abr-review-flow');
    syncModalPresent();
    if (isReplacement) modal.classList.add('abrf-no-intro');
    lastModal = modal;
    lastModalTime = now;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    ensureClose(modal);
    bindSheetDrag(modal);
    watchSheetHeight(modal);

    const close = modal.querySelector('.jdgm-write-review-modal__close-btn');
    if (close) {
      close.setAttribute('aria-label', abrText('close_form', 'Close review form'));
      close.addEventListener('click', (event) => {
        const state = modalStates.get(modal);
        if (!state || state.allowNativeClose) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        dismissModal(modal);
      }, true);
    }

    const title = modal.querySelector('.jdgm-write-review-modal__title');
    if (title) {
      if (!title.id) title.id = 'abr-review-flow-title';
      modal.setAttribute('aria-labelledby', title.id);
    }

    modal.addEventListener('click', (event) => {
      const state = modalStates.get(modal);
      if (state?.autoClick) return;
      const currentPage = visiblePage(modal);
      const star = event.target.closest('.jdgm-write-review-modal__stars .jdgm-star');
      const page = star?.closest('.jdgm-write-review-modal__page');
      const back = event.target.closest('.jdgm-write-review-modal__nav-btn-back');
      const next = event.target.closest('.jdgm-write-review-modal__nav-btn-next');
      const pair = state?.view && reorderedPages(modal);
      if (pair && (back || next) && (state.view === 'share' || state.view === 'details' || state.view === 'submitting')) {
        const onDetails = nativePage(modal) === pair.details;
        if (state.view === 'submitting') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        if (onDetails && ((next && state.view === 'share') || (back && state.view === 'details'))) {
          // Moving between share and About you is ours alone: Judge.me
          // stays on its About you step throughout.
          event.preventDefault();
          event.stopImmediatePropagation();
          state.view = next ? 'details' : 'share';
          state.submitIntent = false;
          syncPage(modal);
          return;
        }
        // Next on About you goes to Judge.me (it validates, then moves
        // to share, which is submitted straight away); Back on share goes
        // to Judge.me's write-review step.
        state.submitIntent = Boolean(next && state.view === 'details');
        // Judge.me handles this click: it sees its own current page.
        returnCurrent(modal);
        if (state.submitIntent) watchDetailsSubmit(modal, state, pair);
      }
      if (star && state?.step === 1 && page?.classList.contains('current-page')) {
        const starGroup = star.closest('.jdgm-write-review-modal__stars');
        state.ratingIndex = Array.from(starGroup.querySelectorAll('.jdgm-star')).indexOf(star);
        prepareSharedTransition(modal, page, 'forward');
      } else if (back && state?.step === 2 && currentPage) {
        prepareSharedTransition(modal, currentPage, 'reverse');
      } else if (next && state?.step === 2 && currentPage) {
        const titleInput = titleInputFor(currentPage);
        const bodyInput = currentPage.querySelector('#jdgm-review-body-input');
        const fields = [titleInput, bodyInput].filter((input) => input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement);
        const invalid = fields.find((input) => !input.value.trim());
        fields.forEach((input) => input === invalid ? showFieldError(input) : clearFieldError(input));
        if (invalid) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          invalid.focus({ preventScroll: true });
        }
      } else if (next && currentPage && state?.step === totalSteps(modal)) {
        // Judge.me's own validation (e.g. an invalid email) can reset this
        // button's text - reassert the correct label right after, since a
        // MutationObserver can't see an <input>'s .value property change.
        // This was previously hardcoded to the default 'Finish' even on
        // the share-media page, which should say 'Submit' - wrong on the
        // one page this branch actually fires for in the current flow.
        const label = isShareMediaPage(currentPage) ? shareMediaLabel(currentPage) : abrText('finish', 'Finish');
        requestAnimationFrame(() => setFinalButtonCopy(currentPage, label));
      }
    }, true);

    modal.addEventListener('click', (event) => {
      const state = modalStates.get(modal);
      if (state?.allowNativeClose) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('.jdgm-write-review-modal__content')) return;
      event.preventDefault();
      event.stopPropagation();
      dismissModal(modal);
    }, true);

    syncPage(modal);

    const observer = new MutationObserver(() => {
      syncOverlayTransition(modal);
      queueSync(modal);
    });
    modalStates.get(modal).observer = observer;
    observer.observe(modal, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    syncNavigationIdle(modal);
    new MutationObserver(() => syncNavigationIdle(modal)).observe(modal, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled']
    });
  }

  function scan(root = document) {
    if (root instanceof Element && root.matches('.jdgm-write-review-modal')) enhance(root);
    root.querySelectorAll?.('.jdgm-write-review-modal').forEach(enhance);
  }

  const documentObserver = new MutationObserver((records) => {
    let removed = false;
    records.forEach((record) => {
      if (record.removedNodes.length) removed = true;
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) scan(node);
      });
    });
    // Only a removal can end it (re-reading the live list walks the page).
    if (removed && document.documentElement.classList.contains('abrf-modal-present')) syncModalPresent();
  });

  function handleEscape(event) {
    if (event.key !== 'Escape') return;
    const modal = Array.from(document.querySelectorAll('.jdgm-write-review-modal')).find(modalIsVisible);
    if (!modal) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    dismissModal(modal);
  }

  function start() {
    scan();
    documentObserver.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('keydown', handleEscape, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
